import React, { useRef, useEffect, useState } from 'react';
import { FriendSearchResult } from '../types/friends';

interface UserSearchProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: FriendSearchResult[];
  isLoading: boolean;
  onConnect: (user: FriendSearchResult) => void;
  onEnterPress?: () => void;
  onUserClick?: (user: FriendSearchResult) => void;
}

export const UserSearch: React.FC<UserSearchProps> = ({
  searchQuery,
  setSearchQuery,
  searchResults,
  isLoading,
  onConnect,
  onEnterPress,
  onUserClick
}) => {
  const searchRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(true);

  // Reset dropdown visibility when search query changes
  useEffect(() => {
    if (searchQuery.length >= 2) {
      setShowDropdown(true);
    }
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown && searchQuery.length >= 2) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown, searchQuery]);

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
          </svg>
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition duration-150 ease-in-out"
          placeholder="Search by name or @username"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && searchQuery.length >= 2 && onEnterPress) {
              setShowDropdown(false);
              onEnterPress();
            }
          }}
        />
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>

      {/* Search Results Dropdown */}
      {showDropdown && searchQuery.length >= 2 && searchResults.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-96 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {searchResults.map((user) => (
            <div
              key={user.userId}
              role={onUserClick ? 'button' : undefined}
              tabIndex={onUserClick ? 0 : undefined}
              onClick={() => onUserClick?.(user)}
              onKeyDown={onUserClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onUserClick(user); } } : undefined}
              className={`flex items-center justify-between px-4 py-3 hover:bg-gray-50 ${onUserClick ? 'cursor-pointer' : ''} ${user.isFriend ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center min-w-0 flex-1">
                <div className="flex-shrink-0 h-10 w-10">
                  {user.profilePictureUrl ? (
                    <img className={`h-10 w-10 rounded-full object-cover ${user.isFriend ? 'grayscale' : ''}`} src={user.profilePictureUrl} alt="" />
                  ) : (
                    <div className={`h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center font-medium text-lg ${user.isFriend ? 'text-gray-400' : 'text-gray-500'}`}>
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className={`text-sm font-medium truncate ${user.isFriend ? 'text-gray-500' : 'text-gray-900'}`}>{user.name}</p>
                  <p className={`text-sm truncate ${user.isFriend ? 'text-gray-400' : 'text-gray-500'}`}>@{user.username || 'unknown'}</p>
                </div>
              </div>
              <div className="ml-4 flex-shrink-0">
                {user.isFriend ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Friend
                  </span>
                ) : user.hasOutgoingRequest ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Pending
                  </span>
                ) : user.hasIncomingRequest ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Requested
                  </span>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onConnect(user);
                    }}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && searchQuery.length >= 2 && !isLoading && searchResults.length === 0 && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md py-4 text-center text-sm text-gray-500 ring-1 ring-black ring-opacity-5">
          No users found matching "{searchQuery}"
        </div>
      )}
    </div>
  );
};
